import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import type { Asset, DesignTool, DesignToolItem } from '@mg/shared';
import { designToolLabel, figmaLinkKind, parseFigmaFileKey } from '@mg/shared';
import { api, actionErrorText } from '../../lib/api';
import { cn } from '../../lib/cn';
import { useTenantKey } from '../../lib/useTenantKey';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Field';
import { EmptyState, LoadingState } from '../../components/ui/States';

/**
 * Import finished artwork FROM Canva or Figma into the Media Library.
 *
 * The two tools need different front doors, which is a property of their APIs rather than a design
 * choice. Canva can be SEARCHED by name, so it gets a search box. Figma has no endpoint listing the
 * teams or files a user can see, so nothing can be browsed from nothing — the user pastes a file
 * URL and we read its frames.
 *
 * Whatever is picked is exported/rendered server-side and stored as a normal library asset, because
 * every URL these vendors hand back expires (Canva ~24h, Figma ~30d).
 */
export function DesignToolImportModal({
  tool,
  open,
  folderId,
  onClose,
  onImported,
  onImportStarted,
}: {
  tool: DesignTool;
  open: boolean;
  /** Media Library folder to file the import under. */
  folderId?: string;
  onClose: () => void;
  /** Fired with the created asset — lets the composer attach it, or the library refresh. */
  onImported?: (asset: Asset) => void;
  /**
   * Called as an import starts; what it returns is called once, as that import ends, landed or not.
   * The modal can be closed while an import runs and the import still lands, so the composer counts
   * it as a file on its way into the post and holds Save until then.
   */
  onImportStarted?: () => () => void;
}) {
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [figmaUrl, setFigmaUrl] = useState('');
  const [fileKey, setFileKey] = useState<string | null>(null);
  const [selected, setSelected] = useState<DesignToolItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setFigmaUrl('');
    setFileKey(null);
    setSelected(null);
    setError(null);
  }, [open]);

  const canvaQuery = useQuery({
    queryKey: ['canva-designs', tenantKey, query],
    queryFn: ({ signal }) => api.searchCanvaDesigns(query, signal),
    enabled: open && tool === 'canva',
  });

  const figmaQuery = useQuery({
    queryKey: ['figma-file', tenantKey, fileKey],
    queryFn: ({ signal }) => api.getFigmaFile(fileKey!, signal),
    enabled: open && tool === 'figma' && fileKey != null,
  });

  const items: DesignToolItem[] =
    tool === 'canva' ? (canvaQuery.data ?? []) : (figmaQuery.data?.frames ?? []);
  const loading = tool === 'canva' ? canvaQuery.isLoading : figmaQuery.isLoading;
  const listError = tool === 'canva' ? canvaQuery.error : figmaQuery.error;

  const importIt = useMutation({
    onMutate: () => onImportStarted?.(),
    mutationFn: () => {
      if (!selected) throw new Error('Nothing selected.');
      return tool === 'canva'
        ? api.importCanvaDesign({ designId: selected.id, folderId, title: selected.title })
        : api.importFigmaFrame({
            fileKey: fileKey!,
            nodeId: selected.id,
            scale: 2,
            folderId,
            title: selected.title,
          });
    },
    onSuccess: (asset) => {
      void queryClient.invalidateQueries({ queryKey: ['media', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['asset-folders', tenantKey] });
      onImported?.(asset);
      onClose();
    },
    onError: (err) => setError(actionErrorText(err, 'Could not import that design.')),
    onSettled: (_asset, _error, _variables, ended) => ended?.(),
  });

  const resolveFigmaUrl = () => {
    const key = parseFigmaFileKey(figmaUrl);
    if (!key) {
      // A Buzz or Slides link IS a Figma link copied from the address bar, so say what is actually
      // wrong: Figma's Files API serves Design (and FigJam) files only.
      const kind = figmaLinkKind(figmaUrl);
      setError(
        kind === 'buzz'
          ? 'Figma Buzz files can’t be imported — Figma doesn’t make Buzz files available to other apps. Export the design from Buzz as a PNG or JPG, then upload it to the Media Library.'
          : kind === 'slides'
            ? 'Figma Slides files can’t be imported — only Figma Design files can. Export the slide as a PNG or JPG, then upload it to the Media Library.'
            : kind === 'other'
              ? 'Only Figma Design files can be imported. Open the design file in Figma and copy the link from your browser’s address bar.'
              : 'That does not look like a Figma file URL — copy the link from your browser’s address bar.',
      );
      return;
    }
    setError(null);
    setSelected(null);
    setFileKey(key);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Import from ${designToolLabel[tool]}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={importIt.isPending}
            disabled={!selected}
            onClick={() => {
              setError(null);
              importIt.mutate();
            }}
          >
            {importIt.isPending ? 'Importing…' : 'Import'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {tool === 'canva' ? (
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" aria-hidden />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your Canva designs…"
              aria-label="Search Canva designs"
              className="pl-9"
            />
          </label>
        ) : (
          <div className="flex gap-2">
            <Input
              value={figmaUrl}
              onChange={(e) => setFigmaUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  resolveFigmaUrl();
                }
              }}
              placeholder="Paste a Figma Design file URL…"
              aria-label="Figma file URL"
            />
            <Button variant="secondary" onClick={resolveFigmaUrl} disabled={!figmaUrl.trim()}>
              Open
            </Button>
          </div>
        )}

        {tool === 'figma' && !fileKey && (
          <p className="text-xs text-ink-faint">
            Figma’s API can’t list your files, so paste the link to the file you want — then pick a
            frame from it. Figma Buzz and Slides files can’t be imported.
          </p>
        )}

        {listError ? (
          <p className="text-sm text-red-600 dark:text-error-dark">{actionErrorText(listError, 'Could not load designs.')}</p>
        ) : loading ? (
          <LoadingState label={`Loading from ${designToolLabel[tool]}…`} />
        ) : (tool === 'canva' || fileKey) && items.length === 0 ? (
          <EmptyState
            title="Nothing to import"
            description={
              tool === 'canva'
                ? 'No designs matched. Try a different search.'
                : 'That file has no top-level frames.'
            }
          />
        ) : items.length > 0 ? (
          <ul className="grid max-h-80 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
            {items.map((item) => {
              const active = selected?.id === item.id;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(item)}
                    aria-pressed={active}
                    className={cn(
                      'focus-ring w-full overflow-hidden rounded-lg border text-left',
                      active ? 'border-action ring-2 ring-action/20' : 'border-line hover:bg-hover',
                    )}
                  >
                    <span className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-page">
                      {item.thumbnailUrl ? (
                        // Vendor-hosted and short-lived — rendered directly, never stored, and NOT
                        // routed through our auth-gated asset proxy.
                        <img
                          src={item.thumbnailUrl}
                          alt=""
                          className="h-full w-full bg-white object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <span className="text-xs text-ink-faint">No preview</span>
                      )}
                    </span>
                    <span className="block truncate px-2 py-1.5 text-xs font-medium text-ink" title={item.title}>
                      {item.title}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
      </div>
    </Modal>
  );
}
